import './card.scss'

import classNames from 'classnames'
import { Link } from 'gatsby'
import _ from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import getCategory from '@/helpers/get-category'
import { getPostSubCategories } from '@/helpers/get-subcategory'
import { useCategories } from '@/hooks/useCategories'

import EventDetail from '../card-event-detail/card-event-detail'
import DynamicImage from '../dynamic-image/dynamic-image'
import SearchTag from '../tag/search-tag'
import { CardCategoryLink } from './card-category-link'

const categorySlugs = {
  UniversityPost: {
    description: 'university',
    name: 'University',
  },
  ProductSingleNews: {
    name: 'Product',
    description: 'product-news',
  },
  MarketUpdate: {
    name: 'Market Updates',
    description: 'market-updates',
  },
  ReportsAndanalysis: {
    name: 'Research',
    description: 'research',
  },
  NFTStory: {
    name: 'NFT Stories',
    description: 'nft-stories',
  },
  CompanySingleNews: {
    name: 'Company News',
    description: 'company-news',
  },
  Event: {
    name: 'Event',
    description: 'events',
  },
}

const getDuration = (item) => {
  return _.get(item, 'acf.readingDuration')
}

const validUri = (item, lang = 'en') => {
  return (
    `/${categorySlugs[item.nodeType].description}${
      !lang.startsWith('en') ? '/' + lang.toLowerCase() : ''
    }/${item?.slug}` ?? ''
  ).replaceAll('%', '-')
}

const isEvent = (item) => {
  const isEventInCategories = _.head(
    _.filter(_.get(item, 'categories.nodes'), (c) => c.slug.startsWith('event'))
  )
  return !_.isEmpty(isEventInCategories)
}

const Card = ({
  item,
  minicard = false,
  hasNoImage = false,
  hasUnderline,
  hasNoLocationDisplay = false,
  hasDate = true,
  isSearch = false,
  isCarousel = false,
  className = '',
  onClick,
  type,
}) => {
  const { t, i18n } = useTranslation()
  const [mainCategory, setMainCategory] = useState({})
  const isevent = useMemo(() => isEvent(item), [item])
  const { withSubCategories } = useCategories()

  const classMore = classNames({
    'card-more': true,
    'card-more-minicard': minicard,
  })
  const classCardContainer = classNames({
    'card-container': true,
    'minicard-container': minicard,
    underline: hasUnderline,
  })
  const classContentMinicard = classNames({
    'card-summary': true,
    'minicard-card-summary': minicard,
  })

  const categoriesInObj = _.get(item, 'categories.nodes')

  const subcategory = getPostSubCategories(item)
  const duration = getDuration(item)

  useEffect(() => {
    const headCategory = getCategory(withSubCategories, categoriesInObj)
    setMainCategory(headCategory)
  }, [withSubCategories, categoriesInObj])

  if (_.isEmpty(item)) return null

  return (
    <div className={`${classCardContainer} ${className}`}>
      <div className="card-frame">
        {!hasNoImage && (
          <a
            className="card-box-image"
            href={validUri(item, i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <DynamicImage
              item={item}
              imageSizes={minicard ? [300, 700, 300] : [300, 700, 540]}
            />
          </a>
        )}
        <div className="card-content">
          <div>
            <CardCategoryLink
              mainCategory={mainCategory}
              subCategory={subcategory}
              minicard={minicard}
              date={_.get(item, 'date')}
              i18n={i18n}
              onClick={onClick}
              type={type}
            />
            <a
              href={validUri(item, i18n.language)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <h2 className="card-title">{_.get(item, 'title', '')}</h2>
            </a>

            {!isCarousel && (
              <div className={classContentMinicard}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: _.get(item, 'excerpt', ''),
                  }}
                />
              </div>
            )}
            {isSearch && (
              <SearchTag categoriesInObj={_.get(item, 'categories.nodes')} />
            )}
            {!isCarousel && !minicard && (
              <p className={classMore}>
                <a
                  href={validUri(item, i18n.language)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {duration ? (
                    t('ui.general.readMore', { count: duration })
                  ) : (
                    <Trans i18nKey="ui.general.more" />
                  )}
                </a>
              </p>
            )}
          </div>
          <div>
            {!isCarousel && minicard && (
              <p className={classMore}>
                <Link to={validUri(item, i18n.language)}>
                  {duration ? (
                    t('ui.general.readMore', { count: duration })
                  ) : (
                    <Trans i18nKey="ui.general.more" />
                  )}
                </Link>
              </p>
            )}
            {isevent && hasDate && (
              <EventDetail
                article={item}
                hasNoLocationDisplay={hasNoLocationDisplay}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Card
